import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { setTransactions } from '../store/salesSlice';
import GravityLogo from './GravityLogo';
import { setProductData } from '../store/productSlice';
import { setAdminData } from '../store/adminSlice';
import { PW_Transactions_List_API, PW_Products_API, PW_Stores_API, PW_Users_API } from '../Server/ApiV2';
import { exportExcel, exportPdf, printReport } from '../utils/exportUtils';
import Spinner from './Spinner';
import { QRCodeSVG } from 'qrcode.react';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import gravityLogo from '../Image/gravity_logo.png';
import Pick6Coupon, { printPick6Coupon } from './Pick6Coupon';
import { PICK6, PICK6_RULE } from '../utils/pick6';

// A bonus win pays the bonus denomination × factor as its regular Prize (there
// is no separate add-on amount); BonusScheduleId is what marks it as a bonus.
// Human-readable win rule printed under the product name (same as mobile).
const getMatchRule = (product) => {
  const method = (product?.matchingMethod || product?.winCondition || '').toLowerCase();
  if (method === 'any3') return 'MATCH 3 NUMBERS ANYWHERE';
  if (method === 'line3') return 'MATCH 3 NUMBERS CONSECUTIVELY';
  if (method === 'any2') return 'MATCH 2 NUMBERS ANYWHERE';
  if (method === PICK6) return PICK6_RULE;
  const name = (product?.name || '').toLowerCase();
  if (name.includes('classic')) return 'MATCH 3 NUMBERS ANYWHERE';
  if (name.includes('supreme')) return 'MATCH 2 NUMBERS ANYWHERE';
  if (name.includes('super')) return 'MATCH 3 NUMBERS CONSECUTIVELY';
  return '';
};
const pad2 = (n) => String(n).padStart(2, '0');

const bonusPrizeOf = (t) => (t && t.isWinner && t.bonusScheduleId) ? (t.prize || 0) : 0;
// Same basis as the P&L tile: a prize is paid out once its ticket is scanned.
const paidPrizeOf = (t) => (t && t.isWinner && t.scannedAt) ? (t.prize || 0) : 0;

const saveBlob = (blob, fileName) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const cardShareText = (t) => {
  const d = new Date(t.timestamp);
  const result = t.isWinner
    ? `WINNER ${(t.prize || 0).toLocaleString()} (${t.scannedAt ? 'Claimed' : 'Unclaimed'})`
    : 'NO PRIZE';
  return [
    'Good Find - Instant Rewards',
    `Card No: ${t.id}`,
    `Date: ${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`,
    t.storeName && `Location: ${t.storeName}`,
    t.productName && `Product: ${t.productName}`,
    `Result: ${result}`,
  ].filter(Boolean).join('\n');
};

export default function Transactions() {
  const dispatch = useDispatch();
  const { transactions } = useSelector(s => s.sales);
  const { products } = useSelector(s => s.product);
  const { stores, users } = useSelector(s => s.admin);
  const [pageLoading, setPageLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [prodRes, storeRes, userRes] = await Promise.all([
        PW_Products_API(), PW_Stores_API(), PW_Users_API(),
      ]);
      if (cancelled) return;
      if (prodRes?.data?.success) dispatch(setProductData(prodRes.data.data));
      if (storeRes?.data?.success) dispatch(setAdminData({ stores: storeRes.data.data }));
      if (userRes?.data?.success) {
        const u = userRes.data.data;
        dispatch(setAdminData({ users: u.users, userStoreRights: u.userStoreRights, userTypes: u.userTypes }));
      }
      setPageLoading(false);
    })();
    return () => { cancelled = true; };
  }, [dispatch]);
  const [applyLoading, setApplyLoading] = useState(false);
  const [viewTxn, setViewTxn] = useState(null);

  const [selStores, setSelStores] = useState([]);
  const [selUsers, setSelUsers] = useState([]);
  const [selProducts, setSelProducts] = useState([]);
  const [selResult, setSelResult] = useState('');
  const [selScanned, setSelScanned] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [timeFrom, setTimeFrom] = useState('');
  const [timeTo, setTimeTo] = useState('');
  const [storeOpen, setStoreOpen] = useState(false);
  const [userOpen, setUserOpen] = useState(false);
  const [productOpen, setProductOpen] = useState(false);

  const [applied, setApplied] = useState({ stores: [], users: [], products: [], result: '', scanned: '', dateFrom: '', dateTo: '', timeFrom: '', timeTo: '' });
  const [hasApplied, setHasApplied] = useState(false);

  const allStores = useMemo(() => stores.map(s => s.name).sort(), [stores]);
  const allUsers = useMemo(() => users.map(u => u.name).sort(), [users]);
  const allProducts = useMemo(() => products.map(p => p.name), [products]); // product order, not A–Z

  const filtered = useMemo(() => {
    let list = transactions;
    if (applied.stores.length) list = list.filter(t => applied.stores.includes(t.storeName));
    if (applied.users.length) list = list.filter(t => applied.users.includes(t.userName));
    if (applied.products.length) list = list.filter(t => applied.products.includes(t.productName));
    if (applied.result === 'win') list = list.filter(t => t.isWinner);
    if (applied.result === 'no') list = list.filter(t => !t.isWinner);
    if (applied.scanned === 'scanned') list = list.filter(t => !!t.scannedAt);
    if (applied.scanned === 'not') list = list.filter(t => !t.scannedAt);
    if (applied.dateFrom) list = list.filter(t => t.timestamp >= applied.dateFrom);
    if (applied.dateTo) list = list.filter(t => t.timestamp <= applied.dateTo + 'T23:59:59');
    if (applied.timeFrom) list = list.filter(t => (t.timestamp.split('T')[1] || '').substring(0, 5) >= applied.timeFrom);
    if (applied.timeTo) list = list.filter(t => (t.timestamp.split('T')[1] || '').substring(0, 5) <= applied.timeTo);
    // Oldest first by sale date and time (the server sends newest first); the id
    // settles a same-millisecond tie.
    return list
      .map(t => ({ t, at: Date.parse(t.timestamp) || 0 }))
      .sort((a, b) => (a.at - b.at) || String(a.t.id).localeCompare(String(b.t.id)))
      .map(x => x.t);
  }, [transactions, applied]);

  // Cash in Hand per row = RSP − prize paid; the rows add up to the P&L tile.
  const cashInHandOf = (t) => (products.find(p => p.id === t.productId)?.rsp || 0) - paidPrizeOf(t);
  // Rows are oldest first, so the running total builds up from the top row down.
  const runningCash = useMemo(() => {
    const rspById = new Map(products.map(p => [p.id, p.rsp || 0]));
    const byTxn = new Map();
    let total = 0;
    filtered.forEach(t => {
      total += (rspById.get(t.productId) || 0) - paidPrizeOf(t);
      byTxn.set(t.id, total);
    });
    return byTxn;
  }, [filtered, products]);

  /* ── Card viewer: Share / Download as a PNG of the receipt + result strip ── */
  const cardRef = useRef(null);
  const cardShot = useRef({ txn: null, promise: null });
  const [cardBusy, setCardBusy] = useState('');
  const [cardNote, setCardNote] = useState('');

  const cardImageFor = useCallback((txn) => {
    if (cardShot.current.txn === txn && cardShot.current.promise) return cardShot.current.promise;
    const promise = (async () => {
      const node = cardRef.current;
      if (!node || node.dataset.txn !== txn.id) throw new Error('The card is not on screen');
      const { default: html2canvas } = await import('html2canvas');
      const shot = await html2canvas(node, {
        scale: 2,
        backgroundColor: null,
        logging: false,
        // The copy must not replay the open animation or clip to the viewer's scroll box.
        onclone: (doc) => {
          const modal = doc.querySelector('.receipt-modal');
          if (modal) Object.assign(modal.style, { animation: 'none', maxHeight: 'none', overflow: 'visible' });
        },
      });
      const pad = 32;
      const out = document.createElement('canvas');
      out.width = shot.width + pad * 2;
      out.height = shot.height + pad * 2;
      const ctx = out.getContext('2d');
      ctx.fillStyle = '#e9ecef';
      ctx.fillRect(0, 0, out.width, out.height);
      ctx.drawImage(shot, pad, pad);
      return new Promise((resolve, reject) => out.toBlob(
        b => (b ? resolve(b) : reject(new Error('Could not create the image'))), 'image/png'));
    })();
    cardShot.current = { txn, promise };
    promise.catch(() => {
      if (cardShot.current.promise === promise) cardShot.current = { txn: null, promise: null };
    });
    return promise;
  }, []);

  // Render as soon as the card has opened: Safari only shows the share sheet when
  // navigator.share runs straight after the click, not after a slow render.
  useEffect(() => {
    setCardNote('');
    setCardBusy('');
    if (!viewTxn) return undefined;
    const timer = setTimeout(() => { cardImageFor(viewTxn).catch(() => {}); }, 400);
    return () => clearTimeout(timer);
  }, [viewTxn, cardImageFor]);

  const handleDownloadCard = async () => {
    const txn = viewTxn;
    setCardNote('');
    setCardBusy('download');
    try {
      saveBlob(await cardImageFor(txn), `${txn.id}.png`);
    } catch {
      setCardNote('Could not create the card image. Please try again.');
    } finally {
      setCardBusy('');
    }
  };

  const handleShareCard = async () => {
    const txn = viewTxn;
    setCardNote('');
    setCardBusy('share');
    try {
      let blob;
      try {
        blob = await cardImageFor(txn);
      } catch {
        setCardNote('Could not create the card image. Please try again.');
        return;
      }
      const file = new File([blob], `${txn.id}.png`, { type: 'image/png' });
      if (navigator.canShare?.({ files: [file] })) {
        try {
          await navigator.share({ files: [file], title: `Card ${txn.id}`, text: cardShareText(txn) });
          return;
        } catch (err) {
          if (err?.name === 'AbortError') return;
        }
      }
      // No share sheet here (e.g. most desktop browsers): hand the image over another way.
      try {
        await navigator.clipboard.write([new window.ClipboardItem({ 'image/png': blob })]);
        setCardNote('Sharing is not available in this browser, so the card image was copied. Paste it into WhatsApp, email or chat.');
      } catch {
        saveBlob(blob, `${txn.id}.png`);
        setCardNote('Sharing is not available in this browser, so the card image was downloaded. Attach it to your message.');
      }
    } finally {
      setCardBusy('');
    }
  };

  // A Pick 6 sale is recognised by its saved options (what its coupon printed).
  const pick6CouponOf = (t) => ({
    txnId: t.id,
    storeSeq: t.storeSeq,
    storeCode: stores.find(s => s.id === t.storeId)?.code,
    timestamp: t.timestamp,
    userName: t.userName,
    storeName: t.storeName,
    productName: t.productName,
    picks: t.cardGrid?.[0] || [],
    options: t.couponOptions,
    factor: t.priceAmount,
  });

  const handlePrintCard = () => {
    setCardNote(printPick6Coupon(pick6CouponOf(viewTxn))
      ? ''
      : 'The browser blocked the print window. Allow pop-ups for this site, then press Print again.');
  };

  const toggleChip = (val, sel, setSel) => setSel(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val]);
  const toggleAll = (all, sel, setSel) => setSel(sel.length === all.length ? [] : [...all]);
  const [filterError, setFilterError] = useState('');
  const handleApply = async () => {
    if (!selStores.length && !selUsers.length && !selProducts.length && !selResult && !selScanned && !dateFrom && !dateTo && !timeFrom && !timeTo) {
      setFilterError('Select at least one filter');
      return;
    }
    if ((dateFrom && !dateTo) || (!dateFrom && dateTo)) {
      setFilterError('Both From Date and To Date are required');
      return;
    }
    if (dateFrom && dateTo && dateFrom > dateTo) {
      setFilterError('From Date cannot be greater than To Date');
      return;
    }
    if ((timeFrom && !timeTo) || (!timeFrom && timeTo)) {
      setFilterError('Both From Time and To Time are required');
      return;
    }
    if (timeFrom && timeTo && timeFrom > timeTo) {
      setFilterError('From Time cannot be greater than To Time');
      return;
    }
    setFilterError('');
    setApplyLoading(true);
    try {
      // Send the filters SQL can apply, so only matching rows come back instead
      // of every transaction ever. The in-browser filters below still run on top
      // (users, scan status, time, multi-select), so what is shown is unchanged.
      const oneId = (sel, list) => {
        const hit = sel.length === 1 ? list.filter(x => x.name === sel[0]) : [];
        return hit.length === 1 ? hit[0].id : undefined;
      };
      const params = {};
      if (dateFrom && dateTo) { params.dateFrom = dateFrom; params.dateTo = dateTo; }
      const sid = oneId(selStores, stores);
      if (sid) params.storeId = sid;
      const pid = oneId(selProducts, products);
      if (pid) params.productId = pid;
      if (selResult === 'win') params.isWinner = true;
      if (selResult === 'no') params.isWinner = false;
      const txnRes = await PW_Transactions_List_API(params);
      if (txnRes?.data?.success) dispatch(setTransactions(txnRes.data.data));
    } finally {
      setApplyLoading(false);
    }
    setHasApplied(true);
    setApplied({ stores: selStores, users: selUsers, products: selProducts, result: selResult, scanned: selScanned, dateFrom, dateTo, timeFrom, timeTo });
  };
  const clearFilters = () => { setSelStores([]); setSelUsers([]); setSelProducts([]); setSelResult(''); setSelScanned(''); setDateFrom(''); setDateTo(''); setTimeFrom(''); setTimeTo(''); setFilterError(''); setHasApplied(false); setApplied({ stores: [], users: [], products: [], result: '', scanned: '', dateFrom: '', dateTo: '', timeFrom: '', timeTo: '' }); };
  const hasFilters = applied.stores.length > 0 || applied.users.length > 0 || applied.products.length > 0 || applied.result || applied.scanned || applied.dateFrom || applied.dateTo || applied.timeFrom || applied.timeTo;

  /* ── Export / Print (Excel · PDF · Print) ── */
  const exportColumns = [
    { key: 'id', label: 'Trans ID' },
    { key: 'selectionNo', label: 'Sel#', format: v => v || '' },
    { key: 'timestamp', label: 'Date', format: v => v ? new Date(v).toLocaleDateString() : '' },
    { key: 'timestamp', label: 'Time', format: v => v ? new Date(v).toLocaleTimeString() : '' },
    { key: 'userName', label: 'User' },
    { key: 'storeName', label: 'Store' },
    { key: 'productName', label: 'Product' },
    { key: 'typeId', label: 'Type' },
    { key: 'isWinner', label: 'Result', format: v => v ? 'Win' : 'No' },
    { key: 'prize', label: 'Prize', num: true, format: (v, r) => (r.isWinner && r.scannedAt) ? (v || 0) : 0 },
    { key: 'prize', label: 'Unclaimed', num: true, format: (v, r) => (r.isWinner && !r.scannedAt) ? (v || 0) : 0 },
    { key: 'bonusScheduleId', label: 'Bonus', num: true, format: (v, r) => bonusPrizeOf(r) },
    { key: 'productId', label: 'Cash in Hand', num: true, format: (v, r) => cashInHandOf(r) },
    { key: 'id', label: 'Running Cash', num: true, format: v => runningCash.get(v) ?? 0 },
    { key: 'scannedAt', label: 'Scanned', format: v => v ? 'Scanned' : 'Not Scanned' },
  ];
  const reportSubtitle = () => {
    const parts = [];
    if (applied.stores.length) parts.push(`Stores: ${applied.stores.join(', ')}`);
    if (applied.products.length) parts.push(`Products: ${applied.products.join(', ')}`);
    if (applied.result) parts.push(`Result: ${applied.result === 'win' ? 'Win' : 'No Prize'}`);
    if (applied.scanned) parts.push(`Scan: ${applied.scanned === 'scanned' ? 'Scanned' : 'Not Scanned'}`);
    if (applied.dateFrom) parts.push(`${applied.dateFrom} → ${applied.dateTo}`);
    return parts.join('  |  ') || 'All transactions';
  };
  const reportKpis = () => {
    const w = filtered.filter(t => t.isWinner);
    // Paid = redeemed (scanned); unclaimed = won but not yet scanned.
    const prize = w.reduce((s, t) => s + (t.scannedAt ? t.prize || 0 : 0), 0);
    const unclaimed = w.reduce((s, t) => s + (!t.scannedAt && t.prize > 0 ? t.prize : 0), 0);
    const bonus = w.reduce((s, t) => s + bonusPrizeOf(t), 0);
    const rev = filtered.reduce((s, t) => s + (products.find(p => p.id === t.productId)?.rsp || 0), 0);
    return [
      { label: 'Total', value: filtered.length },
      { label: 'Winners', value: w.length },
      { label: 'Win Rate', value: `${filtered.length ? (w.length / filtered.length * 100).toFixed(1) : 0}%` },
      { label: 'Revenue', value: rev.toLocaleString() },
      { label: 'Prizes Paid', value: prize.toLocaleString() },
      { label: 'Unclaimed', value: unclaimed.toLocaleString() },
      ...(bonus > 0 ? [{ label: 'Bonus Prizes', value: bonus.toLocaleString() }] : []),
      { label: 'Profit / Loss', value: (rev - prize).toLocaleString() },
    ];
  };
  const doExcel = () => exportExcel('transaction-report', exportColumns, filtered, { kpis: reportKpis() });
  const doPdf = () => exportPdf('transaction-report', 'Transaction Report', exportColumns, filtered, { subtitle: reportSubtitle(), kpis: reportKpis() });
  const doPrint = () => printReport('Transaction Report', exportColumns, filtered, { subtitle: reportSubtitle(), kpis: reportKpis() });
  const canExport = hasApplied && filtered.length > 0;

  const storeRef = useRef(null);
  const userRef = useRef(null);
  const productRef = useRef(null);
  useEffect(() => {
    const handler = (e) => {
      if (storeRef.current && !storeRef.current.contains(e.target)) setStoreOpen(false);
      if (userRef.current && !userRef.current.contains(e.target)) setUserOpen(false);
      if (productRef.current && !productRef.current.contains(e.target)) setProductOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  if (pageLoading) return <div className="page-loading"><Spinner /></div>;

  return (
    <div>
      <div className="page-header">
        <div>
          <h2>Transactions</h2>
          <div className="page-header-sub">Coupon sales transaction log</div>
        </div>
      </div>

      <div className="card">
        <div className="card-title">Transaction Log{hasApplied ? ` (${filtered.length}${hasFilters ? ` of ${transactions.length}` : ''})` : ''}</div>
        <div className="card-body">
          <div className="dash-filters">
            <div className="dash-filter-group" ref={storeRef}>
              <div className="text-sm text-muted mb-4">Store</div>
              <div className="dash-csv-select" onClick={() => setStoreOpen(!storeOpen)}>
                <span>{selStores.length ? selStores.join(', ') : 'All Stores'}</span>
                <span className="dash-csv-arrow">{storeOpen ? '▲' : '▼'}</span>
              </div>
              {storeOpen && (
                <div className="dash-csv-dropdown">
                  <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #e0dce8', padding: '6px 8px' }}>
                    <label className="dash-csv-option" style={{ fontWeight: 600, padding: 0 }}>
                      <input type="checkbox" checked={selStores.length === allStores.length && allStores.length > 0} onChange={() => toggleAll(allStores, selStores, setSelStores)} />
                      <span>Select All</span>
                    </label>
                    {selStores.length > 0 && <span style={{ color: '#e74c3c', cursor: 'pointer', fontSize: 12, alignSelf: 'center' }} onClick={() => setSelStores([])}>Clear All</span>}
                  </div>
                  {allStores.map(s => (
                    <label key={s} className="dash-csv-option">
                      <input type="checkbox" checked={selStores.includes(s)} onChange={() => toggleChip(s, selStores, setSelStores)} />
                      <span>{s}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
            <div className="dash-filter-group" ref={userRef}>
              <div className="text-sm text-muted mb-4">User</div>
              <div className="dash-csv-select" onClick={() => setUserOpen(!userOpen)}>
                <span>{selUsers.length ? selUsers.join(', ') : 'All Users'}</span>
                <span className="dash-csv-arrow">{userOpen ? '▲' : '▼'}</span>
              </div>
              {userOpen && (
                <div className="dash-csv-dropdown">
                  <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #e0dce8', padding: '6px 8px' }}>
                    <label className="dash-csv-option" style={{ fontWeight: 600, padding: 0 }}>
                      <input type="checkbox" checked={selUsers.length === allUsers.length && allUsers.length > 0} onChange={() => toggleAll(allUsers, selUsers, setSelUsers)} />
                      <span>Select All</span>
                    </label>
                    {selUsers.length > 0 && <span style={{ color: '#e74c3c', cursor: 'pointer', fontSize: 12, alignSelf: 'center' }} onClick={() => setSelUsers([])}>Clear All</span>}
                  </div>
                  {allUsers.map(u => (
                    <label key={u} className="dash-csv-option">
                      <input type="checkbox" checked={selUsers.includes(u)} onChange={() => toggleChip(u, selUsers, setSelUsers)} />
                      <span>{u}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
            <div className="dash-filter-group" ref={productRef}>
              <div className="text-sm text-muted mb-4">Product</div>
              <div className="dash-csv-select" onClick={() => setProductOpen(!productOpen)}>
                <span>{selProducts.length ? selProducts.join(', ') : 'All Products'}</span>
                <span className="dash-csv-arrow">{productOpen ? '▲' : '▼'}</span>
              </div>
              {productOpen && (
                <div className="dash-csv-dropdown">
                  <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #e0dce8', padding: '6px 8px' }}>
                    <label className="dash-csv-option" style={{ fontWeight: 600, padding: 0 }}>
                      <input type="checkbox" checked={selProducts.length === allProducts.length && allProducts.length > 0} onChange={() => toggleAll(allProducts, selProducts, setSelProducts)} />
                      <span>Select All</span>
                    </label>
                    {selProducts.length > 0 && <span style={{ color: '#e74c3c', cursor: 'pointer', fontSize: 12, alignSelf: 'center' }} onClick={() => setSelProducts([])}>Clear All</span>}
                  </div>
                  {allProducts.map(p => (
                    <label key={p} className="dash-csv-option">
                      <input type="checkbox" checked={selProducts.includes(p)} onChange={() => toggleChip(p, selProducts, setSelProducts)} />
                      <span>{p}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
            <div className="dash-filter-group">
              <div className="text-sm text-muted mb-4">Result</div>
              <select className="form-input-sm" value={selResult} onChange={e => setSelResult(e.target.value)} style={{ minWidth: 100 }}>
                <option value="">All</option>
                <option value="win">Win</option>
                <option value="no">No Prize</option>
              </select>
            </div>
            <div className="dash-filter-group">
              <div className="text-sm text-muted mb-4">Scan Status</div>
              <select className="form-input-sm" value={selScanned} onChange={e => setSelScanned(e.target.value)} style={{ minWidth: 120 }}>
                <option value="">All</option>
                <option value="scanned">Scanned</option>
                <option value="not">Not Scanned</option>
              </select>
            </div>
            <div className="dash-filter-group">
              <div className="text-sm text-muted mb-4">Date Range</div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input type="date" className="form-input-sm" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
                <span className="text-sm text-muted">to</span>
                <input type="date" className="form-input-sm" value={dateTo} onChange={e => setDateTo(e.target.value)} />
              </div>
            </div>
            <div className="dash-filter-group">
              <div className="text-sm text-muted mb-4">Time Range</div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input type="time" className="form-input-sm" value={timeFrom} onChange={e => setTimeFrom(e.target.value)} />
                <span className="text-sm text-muted">to</span>
                <input type="time" className="form-input-sm" value={timeTo} onChange={e => setTimeTo(e.target.value)} />
              </div>
            </div>
            <div className="dash-filter-actions">
              <button className="btn btn-sm btn-success" onClick={handleApply} disabled={applyLoading}>{applyLoading ? 'Loading…' : 'Apply'}</button>
              {hasFilters && <button className="btn btn-sm btn-outline" onClick={clearFilters}>Clear</button>}
              {canExport && <button className="btn btn-sm btn-outline" onClick={doExcel}>⬇ Excel</button>}
              {canExport && <button className="btn btn-sm btn-outline" onClick={doPdf}>⬇ PDF</button>}
              {canExport && <button className="btn btn-sm btn-outline" onClick={doPrint}>🖨 Print</button>}
            </div>
            {filterError && <div style={{ color: '#e74c3c', fontSize: 13, marginTop: 8, width: '100%' }}>{filterError}</div>}
          </div>
          {!hasApplied && (
            <div className="empty-state">
              <GravityLogo size={48} circle />
              <p>Select filters and click <b>Apply</b> to view transactions.</p>
            </div>
          )}
          {hasApplied && filtered.length > 0 && (() => {
            const wins = filtered.filter(t => t.isWinner);
            const paidWins = wins.filter(t => t.scannedAt);
            const totalPrize = paidWins.reduce((s, t) => s + (t.prize || 0), 0);
            const totalUnclaimed = wins.reduce((s, t) => s + (!t.scannedAt && t.prize > 0 ? t.prize : 0), 0);
            const totalBonus = wins.reduce((s, t) => s + bonusPrizeOf(t), 0);
            const revenue = filtered.reduce((s, t) => s + (products.find(p => p.id === t.productId)?.rsp || 0), 0);
            const pnl = revenue - totalPrize;
            const avgPrize = paidWins.length ? Math.round(totalPrize / paidWins.length) : 0;
            return (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 10, margin: '12px 0 16px' }}>
                <div style={{ background: '#f5f3fa', borderRadius: 8, padding: '10px 14px', textAlign: 'center' }}>
                  <div style={{ fontSize: 20, fontWeight: 700, color: '#2d1b69' }}>{filtered.length}</div>
                  <div style={{ fontSize: 11, color: '#6b6b80', fontWeight: 600 }}>TOTAL</div>
                </div>
                <div style={{ background: '#e8f5e9', borderRadius: 8, padding: '10px 14px', textAlign: 'center' }}>
                  <div style={{ fontSize: 20, fontWeight: 700, color: '#00895e' }}>{wins.length}</div>
                  <div style={{ fontSize: 11, color: '#6b6b80', fontWeight: 600 }}>WINNERS</div>
                </div>
                <div style={{ background: '#f5f3fa', borderRadius: 8, padding: '10px 14px', textAlign: 'center' }}>
                  <div style={{ fontSize: 20, fontWeight: 700, color: '#2d1b69' }}>{filtered.length ? (wins.length / filtered.length * 100).toFixed(1) : 0}%</div>
                  <div style={{ fontSize: 11, color: '#6b6b80', fontWeight: 600 }}>WIN RATE</div>
                </div>
                <div style={{ background: '#f0f7ff', borderRadius: 8, padding: '10px 14px', textAlign: 'center' }}>
                  <div style={{ fontSize: 20, fontWeight: 700, color: '#1565c0' }}>{revenue.toLocaleString()}</div>
                  <div style={{ fontSize: 11, color: '#6b6b80', fontWeight: 600 }}>REVENUE</div>
                </div>
                <div style={{ background: '#fff8e6', borderRadius: 8, padding: '10px 14px', textAlign: 'center' }}>
                  <div style={{ fontSize: 20, fontWeight: 700, color: '#c9a227' }}>{totalPrize.toLocaleString()}</div>
                  <div style={{ fontSize: 11, color: '#6b6b80', fontWeight: 600 }}>PRIZES PAID</div>
                </div>
                <div style={{ background: '#f2f3f6', borderRadius: 8, padding: '10px 14px', textAlign: 'center' }}>
                  <div style={{ fontSize: 20, fontWeight: 700, color: '#7a7f8c' }}>{totalUnclaimed.toLocaleString()}</div>
                  <div style={{ fontSize: 11, color: '#6b6b80', fontWeight: 600 }}>UNCLAIMED</div>
                </div>
                <div style={{ background: '#f5f3fa', borderRadius: 8, padding: '10px 14px', textAlign: 'center' }}>
                  <div style={{ fontSize: 20, fontWeight: 700, color: '#c9a227' }}>{avgPrize.toLocaleString()}</div>
                  <div style={{ fontSize: 11, color: '#6b6b80', fontWeight: 600 }}>AVG PRIZE</div>
                </div>
                {totalBonus > 0 && (
                  <div style={{ background: '#fdf6e3', borderRadius: 8, padding: '10px 14px', textAlign: 'center' }}>
                    <div style={{ fontSize: 20, fontWeight: 700, color: '#c9a227' }}>{totalBonus.toLocaleString()}</div>
                    <div style={{ fontSize: 11, color: '#6b6b80', fontWeight: 600 }}>BONUS PRIZES</div>
                  </div>
                )}
                <div style={{ background: pnl >= 0 ? '#e8f5e9' : '#fde8e8', borderRadius: 8, padding: '10px 14px', textAlign: 'center' }}>
                  <div style={{ fontSize: 20, fontWeight: 700, color: pnl >= 0 ? '#00895e' : '#e74c3c' }}>{pnl.toLocaleString()}</div>
                  <div style={{ fontSize: 11, color: '#6b6b80', fontWeight: 600 }}>P&L</div>
                </div>
              </div>
            );
          })()}
          {hasApplied && filtered.length > 0 && (() => {
            const byProduct = {};
            filtered.forEach(t => {
              const key = t.productId || 'unknown';
              if (!byProduct[key]) {
                const prod = products.find(p => p.id === key);
                byProduct[key] = { name: t.productName || key, rsp: prod?.rsp || 0, total: 0, wins: 0, prizeSum: 0, unclaimedSum: 0 };
              }
              byProduct[key].total++;
              if (t.isWinner) {
                byProduct[key].wins++;
                if (t.scannedAt) byProduct[key].prizeSum += (t.prize || 0);
                else if (t.prize > 0) byProduct[key].unclaimedSum += t.prize;
              }
            });
            // Product order (Classic, Super, Supreme, Elite); an unknown product goes last.
            const rank = (key) => { const i = products.findIndex(p => p.id === key); return i < 0 ? products.length : i; };
            const prodList = Object.entries(byProduct).sort(([a], [b]) => rank(a) - rank(b)).map(([, v]) => v);
            return (
              <div style={{ margin: '0 0 16px' }}>
                <div className="text-sm fw-600 mb-8" style={{ color: '#2d1b69' }}>Product-wise P&L</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 10 }}>
                  {prodList.map(p => {
                    const rev = p.total * p.rsp;
                    const pl = rev - p.prizeSum;
                    return (
                      <div key={p.name} style={{ background: '#fff', border: '1px solid #e0dce8', borderRadius: 10, padding: '12px 16px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                          <span className="fw-700" style={{ color: '#2d1b69', fontSize: 14 }}>{p.name}</span>
                          <span className="chip chip-purple" style={{ fontSize: 10 }}>{p.total} sold</span>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 6, textAlign: 'center' }}>
                          <div>
                            <div style={{ fontSize: 16, fontWeight: 700, color: '#1565c0' }}>{rev.toLocaleString()}</div>
                            <div style={{ fontSize: 10, color: '#6b6b80', fontWeight: 600 }}>REVENUE</div>
                          </div>
                          <div>
                            <div style={{ fontSize: 16, fontWeight: 700, color: '#c9a227' }}>{p.prizeSum.toLocaleString()}</div>
                            <div style={{ fontSize: 10, color: '#6b6b80', fontWeight: 600 }}>PRIZES</div>
                          </div>
                          <div>
                            <div style={{ fontSize: 16, fontWeight: 700, color: '#7a7f8c' }}>{p.unclaimedSum.toLocaleString()}</div>
                            <div style={{ fontSize: 10, color: '#6b6b80', fontWeight: 600 }}>UNCLAIMED</div>
                          </div>
                          <div>
                            <div style={{ fontSize: 16, fontWeight: 700, color: pl >= 0 ? '#00895e' : '#e74c3c' }}>{pl.toLocaleString()}</div>
                            <div style={{ fontSize: 10, color: '#6b6b80', fontWeight: 600 }}>P&L</div>
                          </div>
                        </div>
                        <div style={{ marginTop: 8, display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#6b6b80' }}>
                          <span>{p.wins}W / {p.total - p.wins}L</span>
                          <span>Win {(p.total ? (p.wins / p.total * 100).toFixed(1) : 0)}%</span>
                          <span>RSP {p.rsp}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}
          {hasApplied && filtered.length === 0 && (
            <div className="text-sm text-muted" style={{ padding: 16 }}>No transactions match the selected filters.</div>
          )}
          {hasApplied && filtered.length > 0 && (
            <div style={{ maxHeight: 600, overflow: 'auto' }}>
              <table className="data-table" style={{ fontSize: 12 }}>
                <thead className="sticky-header">
                  <tr>
                    <th style={{ padding: '8px 8px' }}>Trans ID</th>
                    <th style={{ padding: '8px 6px' }}>Sel#</th>
                    <th style={{ padding: '8px 6px' }}>Date</th>
                    <th style={{ padding: '8px 6px' }}>Time</th>
                    <th style={{ padding: '8px 6px' }}>User</th>
                    <th style={{ padding: '8px 6px' }}>Store</th>
                    <th style={{ padding: '8px 6px' }}>Product</th>
                    <th style={{ padding: '8px 6px' }}>Type</th>
                    <th style={{ padding: '8px 6px' }}>Result</th>
                    <th style={{ padding: '8px 6px', textAlign: 'right' }}>Prize</th>
                    <th style={{ padding: '8px 6px', textAlign: 'right' }}>Unclaimed</th>
                    <th style={{ padding: '8px 6px', textAlign: 'right' }}>Bonus</th>
                    <th style={{ padding: '8px 6px', textAlign: 'right', whiteSpace: 'nowrap' }}>Cash in Hand</th>
                    <th style={{ padding: '8px 6px', textAlign: 'right', whiteSpace: 'nowrap' }}>Running Cash</th>
                    <th style={{ padding: '8px 6px', textAlign: 'center' }}>Scanned</th>
                    <th style={{ padding: '8px 6px', textAlign: 'center' }}>Card</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(t => {
                    const cash = cashInHandOf(t);
                    const running = runningCash.get(t.id) ?? 0;
                    return (
                    <tr key={t.id} className={t.isWinner ? 'winner-row' : ''}>
                      <td data-label="Trans ID" style={{ fontFamily: 'monospace', fontSize: 10, padding: '6px 8px', whiteSpace: 'nowrap' }}>{t.id}</td>
                      <td data-label="Sel#" style={{ fontSize: 11, padding: '6px', textAlign: 'center' }}>{t.selectionNo || '—'}</td>
                      <td data-label="Date" style={{ fontSize: 11, padding: '6px', whiteSpace: 'nowrap' }}>{new Date(t.timestamp).toLocaleDateString()}</td>
                      <td data-label="Time" style={{ fontSize: 11, padding: '6px', whiteSpace: 'nowrap' }}>{new Date(t.timestamp).toLocaleTimeString()}</td>
                      <td data-label="User" style={{ fontSize: 11, padding: '6px' }}>{t.userName || '—'}</td>
                      <td data-label="Store" style={{ fontSize: 11, padding: '6px' }}>{t.storeName}</td>
                      <td data-label="Product" style={{ fontSize: 11, padding: '6px' }}>{t.productName}</td>
                      <td data-label="Type" style={{ padding: '6px' }}><span className="chip chip-purple" style={{ fontSize: 10 }}>{t.typeId}</span></td>
                      <td data-label="Result" style={{ padding: '6px' }}>
                        <span className={`chip ${t.isWinner ? 'chip-green' : ''}`} style={{ fontSize: 10 }}>
                          {t.isWinner ? 'Win' : 'No'}
                        </span>
                      </td>
                      <td data-label="Prize" style={{ textAlign: 'right', fontWeight: 600, color: (t.isWinner && t.scannedAt) ? '#c9a227' : '#999', padding: '6px' }}>
                        {(t.isWinner && t.scannedAt) ? t.prize.toLocaleString() : '—'}
                      </td>
                      <td data-label="Unclaimed" style={{ textAlign: 'right', fontWeight: 600, color: (t.isWinner && !t.scannedAt && t.prize > 0) ? '#7a7f8c' : '#999', padding: '6px' }}>
                        {(t.isWinner && !t.scannedAt && t.prize > 0) ? t.prize.toLocaleString() : '—'}
                      </td>
                      <td data-label="Bonus" style={{ textAlign: 'right', fontWeight: 600, color: bonusPrizeOf(t) > 0 ? '#c9a227' : '#999', padding: '6px' }}>
                        {bonusPrizeOf(t) > 0 ? bonusPrizeOf(t).toLocaleString() : '—'}
                      </td>
                      <td data-label="Cash in Hand" style={{ textAlign: 'right', fontWeight: 600, color: cash < 0 ? '#e74c3c' : '#00895e', padding: '6px' }}>
                        {cash.toLocaleString()}
                      </td>
                      <td data-label="Running Cash" style={{ textAlign: 'right', fontWeight: 700, color: running < 0 ? '#e74c3c' : '#2d1b69', padding: '6px' }}>
                        {running.toLocaleString()}
                      </td>
                      <td data-label="Scanned" style={{ textAlign: 'center', padding: '6px' }}>
                        {t.scannedAt ? (
                          <span className="chip chip-green" style={{ fontSize: 10 }} title={`Scanned ${new Date(t.scannedAt).toLocaleString()}${t.scannedBy ? ' by ' + t.scannedBy : ''}`}>Scanned</span>
                        ) : (
                          <span className="chip" style={{ fontSize: 10, background: '#f0f0f0', color: '#888' }}>Not Scanned</span>
                        )}
                      </td>
                      <td data-label="Card" style={{ textAlign: 'center', padding: '6px' }}>
                        {t.cardGrid?.length > 0 ? (
                          <span className="view-card-btn" onClick={() => setViewTxn(t)} title="View instance reward">⊞</span>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {viewTxn && (() => {
        // Mirrors the thermal receipt printed by the mobile app (SunmiPrinterModule.printReceipt):
        // logo, title, details, chosen number box, product + win rule, card grid, QR, footer.
        const d = new Date(viewTxn.timestamp);
        const product = products.find(p => p.id === viewTxn.productId);
        const rule = getMatchRule(product);
        const grid = viewTxn.cardGrid || [];
        const cols = Array.isArray(grid[0]) ? grid[0].length : 4;
        const numbers = grid.flat();
        const isP6 = viewTxn.couponOptions?.length > 0;
        const details = [
          ['Date', `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`],
          ['Time', `${pad2(d.getHours())}:${pad2(d.getMinutes())}`],
          ['User Name', viewTxn.userName],
          ['Card No', viewTxn.id],
          ['Location', viewTxn.storeName],
        ].filter(([, v]) => v);
        return (
          <div className="modal-overlay" onClick={() => setViewTxn(null)}>
            <div className="receipt-modal" onClick={e => e.stopPropagation()}>
              <button className="receipt-close" onClick={() => setViewTxn(null)}>×</button>
              <div ref={cardRef} data-txn={viewTxn.id}>
                {isP6 ? (
                  <div className="receipt-paper" style={{ padding: 0 }}>
                    <Pick6Coupon {...pick6CouponOf(viewTxn)} />
                  </div>
                ) : (
                <div className="receipt-paper">
                  <img src={gravityLogo} alt="" className="receipt-logo" />
                  <div className="receipt-title">Instant Rewards</div>
                  <div className="receipt-subtitle">With Every Fragrance</div>
                  <div className="receipt-divider" />
                  <div className="receipt-details">
                    {details.map(([k, v]) => (
                      <div key={k} className="receipt-detail">
                        <span className="receipt-detail-key">{k}</span>
                        <span className="receipt-detail-sep">:</span>
                        <span className="receipt-detail-val">{v}</span>
                      </div>
                    ))}
                  </div>
                  <div className="receipt-divider" />
                  {viewTxn.selectionNo != null && viewTxn.selectionNo !== '' && (
                    <>
                      <div className="receipt-chosen-label">You have chosen</div>
                      <div className="receipt-chosen-box">{viewTxn.selectionNo}</div>
                    </>
                  )}
                  <div className="receipt-product">{viewTxn.productName || 'Your Card'}</div>
                  {rule && <div className="receipt-rule">{rule}</div>}
                  <div className="receipt-grid" style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}>
                    {numbers.map((val, i) => (
                      <div key={i} className="receipt-cell">{val}</div>
                    ))}
                  </div>
                  <div className="receipt-qr">
                    <QRCodeSVG value={viewTxn.id} size={112} level="M" />
                    <div className="receipt-qr-text">{viewTxn.id}</div>
                  </div>
                  <div className="receipt-divider" />
                  <div className="receipt-footer">Thank you !!!</div>
                </div>
                )}
                <div className={`receipt-result ${viewTxn.isWinner ? 'win' : 'lose'}`}>
                  {viewTxn.isWinner ? (
                    <>
                      <span className="receipt-result-badge">WINNER</span>
                      <span className="receipt-result-prize">{(viewTxn.prize || 0).toLocaleString()}</span>
                      {bonusPrizeOf(viewTxn) > 0 && <span className="receipt-result-bonus">Bonus Prize</span>}
                      <span className="receipt-result-scan">{viewTxn.scannedAt ? 'Claimed' : 'Unclaimed'}</span>
                    </>
                  ) : (
                    <span className="receipt-result-badge">NO PRIZE</span>
                  )}
                </div>
              </div>
              <div className="receipt-toolbar">
                <div className="receipt-actions">
                  {isP6 && (
                    <button type="button" className="btn btn-sm btn-gold" onClick={handlePrintCard} disabled={!!cardBusy}>
                      <PrintOutlinedIcon fontSize="small" />
                      Print
                    </button>
                  )}
                  <button type="button" className="btn btn-sm btn-primary" onClick={handleShareCard} disabled={!!cardBusy}>
                    <ShareOutlinedIcon fontSize="small" />
                    {cardBusy === 'share' ? 'Preparing…' : 'Share'}
                  </button>
                  <button type="button" className="btn btn-sm btn-outline" onClick={handleDownloadCard} disabled={!!cardBusy}>
                    <FileDownloadOutlinedIcon fontSize="small" />
                    {cardBusy === 'download' ? 'Preparing…' : 'Download'}
                  </button>
                </div>
                {cardNote && <div className="receipt-action-note" role="status">{cardNote}</div>}
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
